import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { AdvancedDatepickerComponent } from '@app/shared/components/advanced-datepicker/advanced-datepicker.component';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { Subject, takeUntil, of, from, Observable } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { WarehouseWarrantDetailItem } from '@app/feature/center-business/stock/inbound/models/warehouse-warrant-detail-item';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import { TagInputComponent } from '@app/shared/components/tag-input/tag-input.component';
import { InboundService } from '@app/feature/center-business/stock/inbound/inbound.service';
import { AddLotDialogComponent } from './add-lot-dialog/add-lot-dialog.component';
import { WarehouseWarrantSubmitRequestDTO } from '@app/api/stock-service/model/warehouseWarrantSubmitRequestDTO';
import { WarehouseWarrantSubmitReqDetailDTO } from '@app/api/stock-service/model/warehouseWarrantSubmitReqDetailDTO';
import { WarehouseWarrantSubmitReqBatchDTO } from '@app/api/stock-service/model/warehouseWarrantSubmitReqBatchDTO';
import { SerialDetailDTO } from '@app/api/stock-service/model/serialDetailDTO';
import { SerialProfileManagementService } from '@app/api/stock-service/api/serialProfileManagement.service';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { BarcodeResolverService } from '@app/api/goods-service/api/barcodeResolver.service';
import { BarcodeResolveResultVO } from '@app/api/goods-service/model/barcodeResolveResultVO';
import { Gs1ParseResultVO } from '@app/api/goods-service/model/gs1ParseResultVO';
import { formatDate, parseGS1DateToDate, parseLocalDate } from '@app/shared/utils/date-formatter.util';
import {
  isoDateToLocalDate,
  parseScannedBarcode,
} from '@app/shared/utils/parse-scanned-barcode.util';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { HttpErrorResponse } from '@angular/common/http';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { BarcodeCorrectionLogControllerService } from '@app/api/stock-service/api/barcodeCorrectionLogController.service';
import { BarcodeCorrectionLogCreateDTO } from '@app/api/stock-service/model/barcodeCorrectionLogCreateDTO';
import { BARCODE_CORRECTION_STATUS } from '@app/shared/constants/barcode-correction-status.constants';

// Re-export interfaces from dialog component
export interface TrackingLine {
  id: string;
  skuCode: string;
  skuName: string;
  spuCode: string;
  lot?: string;
  lotFromBarcode?: string;
  expiry?: string;
  expiryDate?: Date;
  serial?: string;
  quantity: number;
  plannedQty?: number;
  policy?: TrackingPolicy;
}

export interface PendingBarcodeCorrection {
  scannedBarcode: string;
  scannedSkuCode?: string;
  // Precomputed once when set (not in the template) so it isn't
  // re-translated on every change-detection tick while the panel is shown.
  message: string;
}

export interface LotOption {
  label: string;
  value: string;
  isAddOption?: boolean;
}

export interface TrackingPolicy {
  requireLot?: boolean;
  requireExpiry?: boolean;
  requireSerial?: boolean;
}

export interface TrackingLineGroup {
  skuCode: string;
  skuName: string;
  spuCode: string;
  lines: TrackingLine[];
  totalQuantity: number;
  isExpanded: boolean;
}

/** A SKU whose received quantity differs from the remaining expected quantity. */
export interface DiscrepancyEntry {
  skuCode: string;
  skuName: string;
  expectedQty: number;
  receivedQty: number;
  reason: string;
  note: string;
}

@Component({
  selector: 'app-receive-goods',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    AppButtonComponent,
    InputTextModule,
    InputNumberModule,
    AdvancedDatepickerComponent,
    TableModule,
    SelectModule,
    DataTableComponent,
    TagInputComponent,
    AddLotDialogComponent,
    DialogModule,
    TextareaModule,
  ],
  providers: [MessageService],
  templateUrl: './receive-goods.component.html',
  styleUrl: './receive-goods.component.css',
})
export class ReceiveGoodsComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly inboundService = inject(InboundService);
  private readonly serialProfileManagementService = inject(SerialProfileManagementService);
  private readonly batchManagementService = inject(BatchManagementService);
  private readonly barcodeResolverService = inject(BarcodeResolverService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly barcodeCorrectionLogControllerService = inject(BarcodeCorrectionLogControllerService);
  readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  pendingBarcodeCorrection: PendingBarcodeCorrection | null = null;

  // Route params
  warrantId: string | null = null;
  warrantCode: string | null = null;

  // Form
  trackingForm!: FormGroup;
  trackingLinesFormArray!: FormArray;
  addTrackingLineForm!: FormGroup;

  // Simple properties
  lotFromBarcode: string | undefined = undefined;

  // Form inputs for manual add
  selectedProduct: WarehouseWarrantDetailItem | null = null;
  previousProductSkuCode: string | null = null; // Track previous product to detect changes
  lotInput = '';
  expiryDateInput: Date | null = null;
  serialInput = '';
  quantityInput = 1;

  // Min date for expiry date picker (today, set to start of day)
  minDate: Date = new Date();

  editingTrackingLineId: string | null = null;

  // Barcode scanning
  formBarcodeInput = '';
  scanningBarcode = false;
  cameraActive = false;
  stream: MediaStream | null = null;

  // Lot management
  lotOptions: string[] = [];
  lotOptionsMap: Map<string, string[]> = new Map();
  /** Lot value/batchId -> its already-registered expiry date (if any), so the
   *  expiry field can be locked to the existing value instead of silently
   *  ignoring a conflicting one on submit. */
  lotExpiryMap: Map<string, string | null> = new Map();
  showAddLotDialog = false;
  selectedSkuForLot: { skuCode: string; skuName: string } | null = null;

  // Tracking lines
  trackingLines: TrackingLine[] = [];
  trackingLineGroups: TrackingLineGroup[] = [];
  expandedSkuGroups = new Set<string>();
  tableDataSource: any[] = [];


  // Receipt discrepancy flow (US-B3-04): shown when received != remaining expected
  warrantInputType: string | null = null;
  showDiscrepancyDialog = false;
  discrepancyEntries: DiscrepancyEntry[] = [];
  // Data
  items: WarehouseWarrantDetailItem[] = [];
  loading = false;
  private skipCanDeactivate = false;

  // Product list for left table
  productListDataSource: any[] = [];
  productListColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      width: 140,
      type: 'text',
      align: 'left',
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      width: 200,
      type: 'text',
      align: 'left',
    },
    {
      label: 'inbound.fields.planned_quantity',
      prop: 'plannedQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
    {
      label: 'inbound.fields.remaining_quantity',
      prop: 'remainingQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
  ];

  // Table columns
  tableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      width: 180,
      type: 'custom',
      align: 'left',
    },
    {
      label: 'inbound.fields.lot',
      prop: 'lot',
      width: 120,
      type: 'custom',
      placeholder: 'inbound.fields.lot',
      align: 'left',
    },
    {
      label: 'inbound.fields.expiry',
      prop: 'expiry',
      width: 150,
      type: 'custom',
      placeholder: 'inbound.fields.expiry',
      align: 'left',
    },
    {
      label: 'inbound.fields.serial',
      prop: 'serial',
      width: 120,
      type: 'custom',
      placeholder: 'inbound.fields.serial',
      align: 'left',
    },
    {
      label: 'inbound.fields.quantity',
      prop: 'quantity',
      width: 90,
      type: 'custom',
      align: 'right',
      min: 0,
    },
    {
      label: 'inbound.fields.planned_quantity',
      prop: 'plannedQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
    {
      label: 'inbound.fields.received_quantity',
      prop: 'receivedQty',
      width: 100,
      type: 'number',
      align: 'right',
    },
    {
      label: 'common.actions.action',
      prop: 'action',
      width: 80,
      type: 'custom',
    },
  ];

  ngOnInit(): void {
    // Expiry must be strictly after today (dd/MM/yyyy picker).
    const minExpiry = new Date();
    minExpiry.setHours(0, 0, 0, 0);
    minExpiry.setDate(minExpiry.getDate() + 1);
    this.minDate = minExpiry;

    this.warrantId = this.route.snapshot.paramMap.get('id');

    if (!this.warrantId) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      return;
    }

    this.initializeForm();
    this.loadWarrantItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCamera();
  }

  // ==================== Helper Methods ====================

  /**
   * Extract SKU codes from items array
   * @param items - Array of warehouse warrant detail items
   * @returns Array of SKU codes
   */
  private extractSkuCodes(items: WarehouseWarrantDetailItem[]): string[] {
    return items
      .map(item => item.skuCode)
      .filter((code): code is string => Boolean(code));
  }

  /**
   * Extract lot numbers from batches array
   * @param batches - Array of batch objects
   * @returns Array of unique lot numbers
   */
  private extractLotNumbers(batches: any[]): string[] {
    return [...new Set(
      batches
        .map(batch => batch.lotValue || batch.batchId)
        .filter(Boolean)
    )];
  }

  /**
   * lot_value has no DB unique constraint (only batch_id does - see V008
   * migration), so the same lot text can legitimately exist under different
   * SKUs. Key lotExpiryMap by skuCode+lotValue, not lotValue alone, otherwise
   * two SKUs sharing a lot name silently leak each other's expiry lock.
   */
  private lotExpiryMapKey(skuCode: string, lotValue: string): string {
    return `${skuCode}::${lotValue}`;
  }

  /**
   * Merge each batch's expiry date (if any) into lotExpiryMap, keyed by
   * skuCode+lot value/batchId.
   */
  private mergeLotExpiryMap(batches: any[]): void {
    batches.forEach((batch) => {
      const skuCode = batch.skuCode;
      const lotValue = batch.lotValue || batch.batchId;
      if (!skuCode || !lotValue) return;
      this.lotExpiryMap.set(this.lotExpiryMapKey(skuCode, lotValue), batch.expiryDate || null);
    });
  }

  /**
   * Group batches by SKU code and extract unique lot numbers
   * @param batches - Array of batch objects
   * @returns Map of SKU code to lot numbers array
   */
  private groupBatchesBySku(batches: any[]): Map<string, string[]> {
    const lotMap = new Map<string, string[]>();

    batches.forEach(batch => {
      const skuCode = batch.skuCode;
      if (!skuCode) return;

      const lotValue = batch.lotValue || batch.batchId;
      if (!lotValue) return;

      if (!lotMap.has(skuCode)) {
        lotMap.set(skuCode, []);
      }

      const lots = lotMap.get(skuCode)!;
      if (!lots.includes(lotValue)) {
        lots.push(lotValue);
      }
    });

    this.mergeLotExpiryMap(batches);
    return lotMap;
  }

  /**
   * Get received quantity from item (with fallback to alreadyQty)
   * @param item - Warehouse warrant detail item
   * @returns Received quantity or undefined
   */
  private getReceivedQuantity(item?: WarehouseWarrantDetailItem): number | undefined {
    if (!item) return undefined;
    return item.receivedQty !== undefined && item.receivedQty !== null
      ? item.receivedQty
      : (item.alreadyQty !== undefined && item.alreadyQty !== null ? item.alreadyQty : undefined);
  }

  /**
   * Calculate remaining quantity for a specific item
   * @param item - Warehouse warrant detail item
   * @returns Remaining quantity (planned - received - current tracking)
   */
  private getRemainingQuantityForItem(item: WarehouseWarrantDetailItem): number {
    const plannedQty = item.plannedQty || 0;
    const receivedQty = this.getReceivedQuantity(item) || 0; // From previous sessions

    // Calculate current total from tracking lines in this session — exclude the line
    // currently being edited, otherwise its own quantity double-counts as "already used"
    // and the max-quantity validator rejects the line's own unchanged value.
    const currentTrackingTotal = this.trackingLines
      .filter(line => line.skuCode === item.skuCode && line.id !== this.editingTrackingLineId)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);

    // Remaining = planned - received (from previous) - current (from this session)
    return Math.max(0, plannedQty - receivedQty - currentTrackingTotal);
  }

  /**
   * Update product list data source with remaining quantity calculation
   * Filters out items with remainingQty <= 0
   * Takes into account both receivedQty from previous sessions and current tracking lines
   */
  private updateProductListDataSource(): void {
    const filteredItems = this.items
      .map(item => {
        const remainingQty = this.getRemainingQuantityForItem(item);

        return {
          skuCode: item.skuCode,
          skuName: item.skuName,
          plannedQty: item.plannedQty || 0,
          remainingQty: remainingQty,
        };
      })
      .filter(item => item.remainingQty > 0); // Filter out items with 0 or less remaining quantity

    this.productListDataSource = filteredItems;

    // Update quantity validators for selected product if any
    this.updateQuantityValidatorsForSelectedProduct();
  }

  /**
   * Show error message when quantity exceeds planned quantity (UI validation only)
   * @param validation - Validation result from validateQuantityNotExceedingPlanned
   * @param quantity - New quantity being added
   * @param skuCode - SKU code
   * @param skuName - SKU name (optional)
   */
  private showQuantityExceedsPlannedError(
    validation: { isValid: boolean; currentTotal: number; plannedQty: number; receivedQty: number },
    quantity: number,
    skuCode: string,
    skuName?: string
  ): void {
    const totalQuantity = validation.receivedQty + validation.currentTotal + quantity;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant('inbound.receive.quantity_exceeds_planned', {
        current: totalQuantity,
        planned: validation.plannedQty,
        sku: skuName || skuCode,
      }),
      life: 5000,
    });
  }

  /**
   * Refresh tracking lines UI (update groups, sync form array, and trigger change detection)
   */
  private refreshTrackingLinesUI(): void {
    this.updateTrackingLineGroups();
    this.syncTrackingLinesToFormArray();
    this.updateQuantityValidatorsForSelectedProduct();
    this.cdr.markForCheck();
  }

  /**
   * Update quantity validators for the currently selected product
   * Called when tracking lines change to ensure validators reflect current remaining quantity
   */
  private updateQuantityValidatorsForSelectedProduct(): void {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (!product) return;

    const remainingQty = this.getRemainingQuantityForItem(product);
    const quantityControl = this.addTrackingLineForm.get('quantity');
    const validators = [
      Validators.required,
      Validators.min(0),
      Validators.max(remainingQty)
    ];
    quantityControl?.setValidators(validators);
    quantityControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Refresh table data and trigger change detection
   */
  private refreshTableData(): void {
    this.flattenTableData();
    this.cdr.markForCheck();
  }

  /**
   * Show error message
   * @param messageKey - Translation key for the message
   * @param params - Optional parameters for translation
   * @param life - Message display duration in ms (default: 5000)
   */
  private showErrorMessage(messageKey: string, params?: any, life: number = 5000): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(messageKey, params),
      life,
    });
  }

  /**
   * Reset add tracking line form to default values
   */
  private resetTrackingLineForm(): void {
    // reset() doesn't clear a disabled state set by applyLotExpiryLock(), so
    // re-enable explicitly before the next lot selection decides again.
    this.addTrackingLineForm.get('expiryDate')?.enable();
    this.addTrackingLineForm.reset({
      product: null,
      lot: '',
      expiryDate: null,
      serial: [],
      quantity: 0,
      barcode: '',
    });
    this.selectedProduct = null;
    this.previousProductSkuCode = null;
  }

  private initializeForm(): void {
    this.trackingForm = this.fb.group({
      trackingLines: this.fb.array([]),
    });
    this.trackingLinesFormArray = this.trackingForm.get('trackingLines') as FormArray;

    this.addTrackingLineForm = this.fb.group({
      product: [null],
      lot: [''],
      expiryDate: [null],
      serial: [[]], // Array of serials
      quantity: [0, [Validators.required, Validators.min(1)]],
      barcode: [''],
    });

    // Watch for product changes
    this.addTrackingLineForm.get('product')?.valueChanges.subscribe((product: WarehouseWarrantDetailItem | null) => {
      const currentSkuCode = product?.skuCode || null;

      // Only clear fields if product actually changed (different SKU code)
      const productChanged = this.previousProductSkuCode !== currentSkuCode;

      // Reuse this dropdown as the correction UI when a barcode scan failed to
      // resolve a product, instead of adding a second/duplicate picker. The
      // audit log entry for the failed scan was already written at scan time
      // (see lookupProductByBarcode), so this only needs to clear the panel.
      if (product && this.pendingBarcodeCorrection) {
        this.pendingBarcodeCorrection = null;
        this.formBarcodeInput = '';
        this.addTrackingLineForm.patchValue({ barcode: '' }, { emitEvent: false });

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('inbound.receive.barcode_correction_applied'),
          life: 3000,
        });
      }

      if (productChanged) {
        // Calculate remaining quantity for the selected product
        const remainingQty = product ? this.getRemainingQuantityForItem(product) : 0;

        // Clear all fields when product changes
        // Mark fields as untouched to avoid showing validation errors immediately
        this.addTrackingLineForm.patchValue({
          lot: '',
          expiryDate: null,
          serial: [], // Clear serials whenever product changes
          quantity: Math.max(0, remainingQty), // Set default quantity to remaining quantity
        }, { emitEvent: false }); // Don't emit event to avoid triggering quantity update

        // lot was just cleared above - no lot selected means no expiry lock applies.
        this.applyLotExpiryLock(product?.skuCode, null);

        // Mark fields as untouched so validation errors don't show until user clicks "Thêm"
        this.addTrackingLineForm.get('lot')?.markAsUntouched();
        this.addTrackingLineForm.get('lot')?.markAsPristine();
        this.addTrackingLineForm.get('expiryDate')?.markAsUntouched();
        this.addTrackingLineForm.get('serial')?.markAsUntouched();
        this.addTrackingLineForm.get('quantity')?.markAsUntouched();

        // Update quantity validators based on remaining quantity
        if (product) {
          const quantityControl = this.addTrackingLineForm.get('quantity');
          const validators = [
            Validators.required,
            Validators.min(0),
            Validators.max(remainingQty)
          ];
          quantityControl?.setValidators(validators);
          quantityControl?.updateValueAndValidity({ emitEvent: false });
        }
      }

      // Update previous product SKU
      this.previousProductSkuCode = currentSkuCode;

      this.updateFieldValidators(product);
      if (product) {
        this.loadLotNumbers(product.skuCode || '');
      } else {
        this.lotOptions = [];
      }

      // Trigger change detection to update UI
      this.cdr.markForCheck();
    });

    // Watch for quantity changes - validate in real-time
    this.addTrackingLineForm.get('quantity')?.valueChanges.subscribe((quantity: number) => {
      const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
      if (!product) return;

      const remainingQty = this.getRemainingQuantityForItem(product);
      const quantityControl = this.addTrackingLineForm.get('quantity');

      if (quantity > remainingQty) {
        // Set error only (no warning message)
        quantityControl?.setErrors({ max: { max: remainingQty, actual: quantity } });
        quantityControl?.markAsTouched();
      } else {
        // Clear max error if quantity is valid
        const errors = quantityControl?.errors;
        if (errors?.['max']) {
          delete errors['max'];
          if (Object.keys(errors).length === 0) {
            quantityControl?.setErrors(null);
          } else {
            quantityControl?.setErrors(errors);
          }
        }
      }
    });

    // Watch for serial changes - force quantity to match serial count
    this.addTrackingLineForm.get('serial')?.valueChanges.subscribe((serials: string[]) => {
      if (Array.isArray(serials) && serials.length > 0) {
        // If serials exist, force quantity to match serial count
        const currentQuantity = this.addTrackingLineForm.get('quantity')?.value || 0;
        if (currentQuantity !== serials.length) {
          this.addTrackingLineForm.patchValue({ quantity: serials.length }, { emitEvent: false });
        }
      }
    });
  }

  private loadWarrantItems(): void {
    if (!this.warrantId) return;

    this.loading = true;
    const warrantIdNum = parseInt(this.warrantId, 10);
    if (isNaN(warrantIdNum)) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      this.loading = false;
      return;
    }

    this.inboundService
      .getWarehouseWarrantDetail(warrantIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.warrantCode = response?.code || null;
          this.warrantInputType = response?.inputType || null;

          // Extract items from response - try different possible property names
          const itemDetailsData =
            response?.warehousewarrantDetailList ||
            response?.warehouseWarrantDetailList ||
            response?.warehouseWarrantDetails ||
            response?.itemDetails ||
            response?.items;

          if (itemDetailsData && Array.isArray(itemDetailsData)) {
            this.items = itemDetailsData;
          } else {
            this.items = [];
          }

          // Update product list data source
          this.updateProductListDataSource();

          // Load SPU policies
          this.loadSpuPolicies();
        },
        error: (error: any) => {
          console.error('Error loading warrant items:', error);
          this.showErrorMessage('inbound.receive.failed_to_load_items');
          this.loading = false;
        },
      });
  }

  private loadSpuPolicies(): void {
    if (!this.items || this.items.length === 0) {
      this.loading = false;
      return;
    }

    const skuCodes = this.extractSkuCodes(this.items);
    if (skuCodes.length === 0) {
      this.loading = false;
      return;
    }

    // Load serial profiles by SKU codes using the new API
    this.serialProfileManagementService
      .getSerialProfileMapBySkuCodes(skuCodes, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        catchError((error) => {
          console.error('Error loading serial profiles:', error);
          return of({});
        })
      )
      .subscribe({
        next: (profileMap: { [key: string]: any }) => {
          // Map profiles to items directly by SKU code
          this.items.forEach(item => {
            const profile = profileMap[item.skuCode || ''];
            if (profile) {
              // Update item policy from serial profile
              item.policy = {
                requireLot: profile.lotRequired || false,
                requireExpiry: profile.expiryRequired || false,
                requireSerial: profile.serialRequired || false,
              };
            }
          });

          this.cdr.markForCheck();
          // After loading serial profiles, load batch information for all SKUs
          this.loadAllBatchInfo();
        },
        error: (error) => {
          console.error('Error loading SPU policies:', error);
          this.loading = false;
        },
      });
  }

  /**
   * Load batch information for all SKUs in the warrant
   */
  private loadAllBatchInfo(): void {
    if (!this.items || this.items.length === 0) {
      this.loading = false;
      return;
    }

    const skuCodes = this.extractSkuCodes(this.items);
    if (skuCodes.length === 0) {
      this.loading = false;
      return;
    }

    // Load batches by SKU codes using the new API
    this.batchManagementService
      .getBatchListBySkuCodes(skuCodes, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        map((data: any) => Array.isArray(data) ? data : []),
        map((batches: any[]) => this.groupBatchesBySku(batches)),
        catchError((error) => {
          console.error('Error loading batch information:', error);
          return of(new Map<string, string[]>());
        })
      )
      .subscribe({
        next: (lotMap: Map<string, string[]>) => {
          this.lotOptionsMap = lotMap;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading batch information:', error);
          this.loading = false;
        },
      });
  }

  onCancel(): void {
    const tab = this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
    this.router.navigate(['/stock/inbound'], { queryParams: { tab } });
  }

  onConfirm(): void {
    if (this.trackingLines.length === 0) {
      this.showErrorMessage('inbound.receive.at_least_one_tracking_line_required');
      return;
    }

    // Validate required fields based on policy
    const validationErrors = this.validateTrackingLines();
    if (validationErrors.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: validationErrors.join(', '),
      });
      return;
    }

    if (!this.warrantId) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      return;
    }

    const warrantIdNum = parseInt(this.warrantId, 10);
    if (isNaN(warrantIdNum)) {
      this.showErrorMessage('inbound.receive.invalid_warrant_id');
      return;
    }

    // US-B3-04: for purchase-order warrants, a variance between received and
    // remaining expected quantity requires a recorded reason before submit.
    if (this.isPurchaseOrderWarrant()) {
      this.discrepancyEntries = this.findDiscrepantSkus();
      if (this.discrepancyEntries.length > 0) {
        this.showDiscrepancyDialog = true;
        return;
      }
    }

    this.doSubmit(warrantIdNum);
  }

  /** Warrant linked to a purchase order (supplier receiving flow). */
  private isPurchaseOrderWarrant(): boolean {
    return this.warrantInputType === 'PURCHASE_ORDER';
  }

  /**
   * SKUs whose submitted quantity differs from the remaining expected quantity
   * (planned - already received). Reasons are mandatory for PO warrants.
   */
  private findDiscrepantSkus(): DiscrepancyEntry[] {
    const receivedBySku = new Map<string, number>();
    this.trackingLines.forEach((line) => {
      receivedBySku.set(
        line.skuCode,
        (receivedBySku.get(line.skuCode) || 0) + (line.quantity || 0),
      );
    });

    const entries: DiscrepancyEntry[] = [];
    receivedBySku.forEach((received, skuCode) => {
      const item = this.items.find((i) => i.skuCode === skuCode);
      if (!item) return;
      const expected = Math.max(
        0,
        (item.plannedQty || 0) - (this.getReceivedQuantity(item) || 0),
      );
      if (received !== expected) {
        entries.push({
          skuCode,
          skuName: item.skuName || '',
          expectedQty: expected,
          receivedQty: received,
          reason: '',
          note: '',
        });
      }
    });
    return entries;
  }

  /** All discrepancy rows must carry a reason before the warrant can be submitted. */
  get allDiscrepancyReasonsFilled(): boolean {
    return this.discrepancyEntries.every((e) => e.reason.trim().length > 0);
  }

  /** Confirm the discrepancy dialog and continue the submit. */
  confirmDiscrepancyAndSubmit(): void {
    if (!this.allDiscrepancyReasonsFilled) {
      return;
    }
    const warrantIdNum = parseInt(this.warrantId || '', 10);
    if (isNaN(warrantIdNum)) {
      return;
    }
    this.showDiscrepancyDialog = false;
    this.doSubmit(warrantIdNum);
  }

  private doSubmit(warrantIdNum: number): void {
    this.loading = true;

    // Transform tracking lines to API payload format
    const submitData = this.transformTrackingLinesToBatchPayload(
      this.trackingLines,
      this.discrepancyEntries,
    );

    // Call API to save tracking lines
    this.inboundService
      .submitInboundWarrant(warrantIdNum, submitData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.skipCanDeactivate = true;
          this.trackingForm?.markAsPristine();
          this.addTrackingLineForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('inbound.receive.saved_successfully'),
          });

          // Navigate back to list page with tab
          setTimeout(() => {
            this.router.navigate(['/stock/inbound'], { queryParams: { tab: 'inbound-warrant' } });
          }, 1000);
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          console.error('Error saving tracking lines:', error);
          const errorCode = error.error?.errorCode ?? '';
          if (errorCode === 'CONCURRENT_MODIFICATION') {
            this.errorDialogService.showError(
              this.translateService.instant('common.messages.concurrent_modification'),
            );
            return;
          }
          const message = error.error?.message ?? error.message ?? '';
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              message,
              'inbound.receive.failed_to_save',
            ),
          );
        },
      });
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }

    return !(
      (this.trackingForm?.dirty ?? false) ||
      (this.addTrackingLineForm?.dirty ?? false)
    );
  }

  // Copy all methods from receive-goods-dialog.component.ts
  // For brevity, I'll include key methods - you may need to copy all methods

  private updateFieldValidators(product: WarehouseWarrantDetailItem | null): void {
    const lotControl = this.addTrackingLineForm.get('lot');
    const expiryControl = this.addTrackingLineForm.get('expiryDate');
    const serialControl = this.addTrackingLineForm.get('serial');

    if (!product || !product.policy) {
      lotControl?.clearValidators();
      expiryControl?.clearValidators();
      serialControl?.clearValidators();
    } else {
      const policy = product.policy;
      if (policy.requireLot) {
        lotControl?.setValidators([Validators.required]);
      } else {
        lotControl?.clearValidators();
      }

      if (policy.requireExpiry) {
        expiryControl?.setValidators([Validators.required]);
      } else {
        expiryControl?.clearValidators();
      }

      if (policy.requireSerial) {
        serialControl?.setValidators([Validators.required]);
      } else {
        serialControl?.clearValidators();
      }
    }

    lotControl?.updateValueAndValidity({ emitEvent: false });
    expiryControl?.updateValueAndValidity({ emitEvent: false });
    serialControl?.updateValueAndValidity({ emitEvent: false });
  }

  private loadLotNumbers(skuCode: string): void {
    if (!skuCode) {
      this.lotOptions = [];
      return;
    }

    // First, try to get from cached map
    if (this.lotOptionsMap.has(skuCode)) {
      this.lotOptions = this.lotOptionsMap.get(skuCode) || [];
      this.cdr.markForCheck();
      return;
    }

    // If not in cache, load from API (fallback for when batch info wasn't loaded initially)
    this.batchManagementService
      .getBatchListBySkuCodes([skuCode], 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        map((data: any) => Array.isArray(data) ? data : []),
        map((batches: any[]) => {
          const lotNumbers = this.extractLotNumbers(batches);
          this.mergeLotExpiryMap(batches);
          // Cache the result
          this.lotOptionsMap.set(skuCode, lotNumbers);
          return lotNumbers;
        }),
        catchError((error) => {
          console.error('Error loading batches:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (lotNumbers: string[]) => {
          this.lotOptions = lotNumbers;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading lot numbers:', error);
          this.lotOptions = [];
        },
      });
  }

  /**
   * Check if a tracking line requires lot, expiry, or serial based on policy
   * @param line Tracking line to check
   * @returns true if line requires lot, expiry, or serial
   */
  private requiresAdditionalInfo(line: TrackingLine): boolean {
    const policy = line.policy;
    if (!policy) return false;

    return policy.requireLot === true ||
           policy.requireExpiry === true ||
           policy.requireSerial === true;
  }

  /**
   * Validate tracking lines - check if any line requires additional info but is missing it
   * @returns Array of error messages
   */
  private validateTrackingLines(): string[] {
    const errors: string[] = [];

    this.trackingLines.forEach((line, index) => {
      // Only validate lines that require additional info
      if (!this.requiresAdditionalInfo(line)) {
        return;
      }

      const policy = line.policy;
      if (!policy) return;

      const lineNumber = index + 1;
      const skuInfo = `${line.skuCode} (${line.skuName})`;

      if (policy.requireLot && !line.lot?.trim()) {
        errors.push(
          this.translateService.instant('inbound.receive.lot_required', {
            line: lineNumber,
            sku: skuInfo,
          })
        );
      }

      if (policy.requireExpiry && !line.expiry?.trim()) {
        errors.push(
          this.translateService.instant('inbound.receive.expiry_required', {
            line: lineNumber,
            sku: skuInfo,
          })
        );
      }

      if (policy.requireSerial && !line.serial?.trim()) {
        errors.push(
          this.translateService.instant('inbound.receive.serial_required', {
            line: lineNumber,
            sku: skuInfo,
          })
        );
      }
    });
    return errors;
  }

  /**
   * Check if there are any validation errors
   */
  get hasValidationErrors(): boolean {
    return this.validateTrackingLines().length > 0;
  }

  isFormFieldRequired(field: 'lot' | 'expiry' | 'serial'): boolean {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (!product || !product.policy) {
      return false;
    }

    const policy = product.policy;
    switch (field) {
      case 'lot':
        return policy.requireLot === true;
      case 'expiry':
        return policy.requireExpiry === true;
      case 'serial':
        return policy.requireSerial === true;
      default:
        return false;
    }
  }

  getProductOptions(): WarehouseWarrantDetailItem[] {
    // Filter out items with remainingQty <= 0
    return this.items.filter(item => this.getRemainingQuantityForItem(item) > 0);
  }

  getSelectedProductPlannedQty(): string {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (!product || product.plannedQty === undefined || product.plannedQty === null) {
      return '-';
    }
    return product.plannedQty.toString();
  }

  getSelectedProductReceivedQty(): string {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (!product) {
      return '-';
    }
    const receivedQty = this.getReceivedQuantity(product) ?? 0;
    return receivedQty.toString();
  }

  getSelectedProductCurrentQty(): string {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (!product || !product.skuCode) {
      return '-';
    }

    // Calculate total quantity from tracking lines in table for this SKU
    const totalQuantity = this.trackingLines
      .filter(line => line.skuCode === product.skuCode)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);

    // Always return total from tracking lines (even if 0)
    return totalQuantity.toString();
  }

  getLotOptionsWithAdd(): LotOption[] {
    const options: LotOption[] = this.lotOptions.map(lot => ({
      label: lot,
      value: lot,
    }));

    options.push({
      label: this.translateService.instant('inbound.receive.add_new_lot'),
      value: '__ADD_NEW_LOT__',
      isAddOption: true,
    });

    return options;
  }

  onFormLotChange(event: any): void {
    const selectedValue = event.value;
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    if (selectedValue === '__ADD_NEW_LOT__') {
      // A lot not yet created has no known expiry - always re-enable here,
      // otherwise the field stays stuck disabled if it was locked by a
      // previously-selected existing lot.
      this.applyLotExpiryLock(product?.skuCode, null);
      if (product) {
        this.selectedSkuForLot = {
          skuCode: product.skuCode || '',
          skuName: product.skuName || '',
        };
        this.showAddLotDialog = true;
        this.addTrackingLineForm.patchValue({ lot: '' });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant('inbound.receive.select_product_first'),
          life: 3000,
        });
        this.addTrackingLineForm.patchValue({ lot: '' });
      }
      return;
    }

    this.applyLotExpiryLock(product?.skuCode, selectedValue, this.editingTrackingLineId);
  }

  /**
   * p-select's built-in clear ("x") button sets the control's value via
   * ControlValueAccessor.writeValue()/onModelChange() only - it deliberately
   * does NOT emit (onChange) (see PrimeNG Select.clear() -> updateModel()),
   * so onFormLotChange() never runs from it. Without this handler, clearing
   * the lot left expiryDate stuck at whatever lock state the previous lot
   * selection set, even though no lot is selected anymore.
   */
  onFormLotCleared(): void {
    const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
    this.applyLotExpiryLock(product?.skuCode, null, this.editingTrackingLineId);
  }

  /**
   * An existing lot that already has an expiry date on file must keep it -
   * the backend silently ignores a different value submitted for an
   * already-set batch, so lock the field here instead of letting the user
   * type a value that will be discarded without warning.
   *
   * lot_value has no DB unique constraint (only batch_id does), so lookups
   * must be scoped by skuCode - see lotExpiryMapKey().
   */
  private lockedExpiry: Date | null = null;

  private applyLotExpiryLock(
    skuCode: string | null | undefined,
    lotValue: string | null | undefined,
    excludeLineId?: string | null
  ): void {
    const expiryControl = this.addTrackingLineForm.get('expiryDate');
    if (!expiryControl) {
      return;
    }

    const existingExpiry =
      skuCode && lotValue
        ? this.getKnownExpiryForLot(skuCode, lotValue, excludeLineId)
        : null;
    if (existingExpiry) {
      expiryControl.setValue(parseLocalDate(existingExpiry));
      expiryControl.disable();
      this.lockedExpiry = expiryControl.value;
    } else {
      if (this.lockedExpiry && expiryControl.value === this.lockedExpiry) {
        expiryControl.setValue(null);
      }
      this.lockedExpiry = null;
      expiryControl.enable();
    }
  }

  /**
   * lotExpiryMap only reflects the server's already-persisted Batch.expiryDate.
   * A brand-new lot created in this session has no expiry on the server yet
   * (create5 only sends batchId+skuCode), so it stays unlocked until submit -
   * but a not-yet-submitted local tracking line for that same SKU+lot may
   * already carry a typed expiry. Without checking it too, a second local
   * add for the same new lot with a different (or blank) expiry would go
   * through unlocked, and the two conflicting values would only surface much
   * later: normalizeBatches() (backend) silently keeps whichever value
   * reached the batch first and drops the other, while each serial's own
   * expiryDate is persisted independently - leaving Batch.expiryDate
   * mismatched against a linked SerialNumber.expiryDate with no error raised
   * anywhere. Local, not-yet-submitted lines are the freshest truth, so they
   * take priority over the (possibly stale/absent) server value.
   */
  private getKnownExpiryForLot(
    skuCode: string,
    lotValue: string,
    excludeLineId?: string | null
  ): string | null {
    const localLine = this.trackingLines.find(
      (line) =>
        line.id !== excludeLineId &&
        line.skuCode === skuCode &&
        line.lot === lotValue &&
        !!line.expiry
    );
    if (localLine?.expiry) {
      return localLine.expiry;
    }
    return this.lotExpiryMap.get(this.lotExpiryMapKey(skuCode, lotValue)) ?? null;
  }

  onAddLotConfirm(lotNumber: string): void {
    if (!this.selectedSkuForLot || !lotNumber.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('inbound.receive.lot_number_required'),
        life: 3000,
      });
      return;
    }

    const skuCode = this.selectedSkuForLot.skuCode;
    const trimmedLotNumber = lotNumber.trim();

    // Call API to create new batch
    const batchCreateRequest = {
      batchId: trimmedLotNumber,
      skuCode: skuCode,
    };

    this.batchManagementService
      .create5(batchCreateRequest, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        switchMap(() => {
          // After creating batch, reload batch info for all SKUs
          return this.reloadBatchInfoForSku(skuCode);
        }),
        catchError((error) => {
          console.error('Error creating batch:', error);
          const fallbackKey = 'inbound.receive.failed_to_save';
          this.errorDialogService.showError(this.errorTranslatorService.translate(error.error.message, fallbackKey));
          return of(null);
        })
      )
      .subscribe({
        next: (result) => {
          if (result !== null) {
            // Update lot options for current SKU
            if (this.lotOptionsMap.has(skuCode)) {
              const lots = this.lotOptionsMap.get(skuCode) || [];
              if (!lots.includes(trimmedLotNumber)) {
                lots.push(trimmedLotNumber);
                this.lotOptionsMap.set(skuCode, lots);
              }
            } else {
              this.lotOptionsMap.set(skuCode, [trimmedLotNumber]);
            }

            // Update current lot options if this SKU is selected
            const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
            if (product && product.skuCode === skuCode) {
              this.lotOptions = this.lotOptionsMap.get(skuCode) || [];
            }

            // Set the lot number in form
            this.addTrackingLineForm.patchValue({ lot: trimmedLotNumber });
            // reloadBatchInfoForSku (above) already refreshed lotExpiryMap for
            // this SKU, so this correctly re-enables the field for the
            // brand-new lot (no expiry on file yet) unless a not-yet-submitted
            // local line already typed one in for it (see getKnownExpiryForLot).
            this.applyLotExpiryLock(skuCode, trimmedLotNumber, this.editingTrackingLineId);

            // Show success message
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant('inbound.receive.tracking_line_added'),
              life: 3000,
            });
          }

          this.showAddLotDialog = false;
          this.selectedSkuForLot = null;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error in onAddLotConfirm:', error);
        },
      });
  }

  /**
   * Reload batch information for a specific SKU
   */
  private reloadBatchInfoForSku(skuCode: string): Observable<string[]> {
    return this.batchManagementService
      .getBatchListBySkuCodes([skuCode], 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
        map((data: any) => Array.isArray(data) ? data : []),
        map((batches: any[]) => {
          const lotNumbers = this.extractLotNumbers(batches);
          this.mergeLotExpiryMap(batches);

          // Update the map
          this.lotOptionsMap.set(skuCode, lotNumbers);

          // Update current lot options if this SKU is selected
          const product = this.addTrackingLineForm.get('product')?.value as WarehouseWarrantDetailItem | null;
          if (product && product.skuCode === skuCode) {
            this.lotOptions = lotNumbers;
          }

          return lotNumbers;
        }),
        catchError((error) => {
          console.error('Error reloading batch info:', error);
          return of([]);
        })
      );
  }

  /**
   * Validate if total quantity (received + current + new) exceeds planned quantity
   * @param skuCode SKU code to validate
   * @param newQuantity New quantity to add/update
   * @param excludeLineId Optional: ID of tracking line to exclude from current total (for update case)
   * @returns true if valid (not exceeding), false if exceeding
   */
  private validateQuantityNotExceedingPlanned(
    skuCode: string,
    newQuantity: number,
    excludeLineId?: string
  ): { isValid: boolean; currentTotal: number; plannedQty: number; receivedQty: number } {
    // Find the product item
    const product = this.items.find(item => item.skuCode === skuCode);
    if (!product) {
      return { isValid: true, currentTotal: 0, plannedQty: 0, receivedQty: 0 };
    }

    const plannedQty = product.plannedQty || 0;
    // Get received quantity from previous sessions
    const receivedQty = product.receivedQty !== undefined && product.receivedQty !== null
      ? product.receivedQty
      : (product.alreadyQty !== undefined && product.alreadyQty !== null ? product.alreadyQty : 0);

    // Calculate current total from tracking lines (excluding the line being edited if provided)
    const currentTotal = this.trackingLines
      .filter(line => line.skuCode === skuCode && line.id !== excludeLineId)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);

    // Total = receivedQty (from previous sessions) + currentTotal (from current session) + newQuantity
    const totalQuantity = receivedQty + currentTotal + newQuantity;

    return {
      isValid: totalQuantity <= plannedQty,
      currentTotal: currentTotal,
      plannedQty: plannedQty,
      receivedQty: receivedQty,
    };
  }

  /**
   * Handle add tracking line from form
   */
  onAddTrackingLine(): void {
    // Mark all fields as touched to show validation errors
    this.addTrackingLineForm.markAllAsTouched();

    // Validate form
    if (!this.addTrackingLineForm.valid) {
      // Form is invalid, errors will be shown under each input
      return;
    }

    const formValue = this.addTrackingLineForm.getRawValue();
    const selectedItem = formValue.product as WarehouseWarrantDetailItem | null;

    // Product is REQUIRED - cannot add without product
    if (!selectedItem) {
      // Mark product field as invalid
      this.addTrackingLineForm.get('product')?.setErrors({ required: true });
      this.addTrackingLineForm.get('product')?.markAsTouched();
      this.showErrorMessage('inbound.receive.product_required');
      return;
    }

    // Prepare tracking line data
    const lot = formValue.lot?.trim() || undefined;
    const expiry = formValue.expiryDate
      ? formatDate(formValue.expiryDate)
      : undefined;
    const expiryDate = formValue.expiryDate ? new Date(formValue.expiryDate) : undefined;
    // Serial is now an array from chips, join with comma for storage
    const serialsArray = Array.isArray(formValue.serial) ? formValue.serial : [];
    const serial = serialsArray.length > 0 ? serialsArray.join(',') : undefined;
    // If serials exist, force quantity to match serial count
    const quantity = serialsArray.length > 0 ? serialsArray.length : (formValue.quantity || 1);
    const skuCode = selectedItem.skuCode || '';

    // Check if we're editing an existing line
    if (this.editingTrackingLineId) {
      // Find and update the existing tracking line
      const existingLine = this.trackingLines.find(line => line.id === this.editingTrackingLineId);
      if (existingLine) {
        // Validate quantity before update
        const validation = this.validateQuantityNotExceedingPlanned(
          skuCode,
          quantity,
          this.editingTrackingLineId
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlannedError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }

        existingLine.skuCode = skuCode;
        existingLine.skuName = selectedItem.skuName || '';
        existingLine.spuCode = selectedItem.spuCode || '';
        existingLine.lot = lot;
        existingLine.expiry = expiry;
        existingLine.expiryDate = expiryDate;
        existingLine.serial = serial;
        existingLine.quantity = quantity;
        existingLine.plannedQty = selectedItem.plannedQty;
        existingLine.policy = selectedItem.policy;

        // Update groups and form array
        this.refreshTrackingLinesUI();

        // Reset edit mode
        this.editingTrackingLineId = null;

        // Update table data to remove edit border
        this.refreshTableData();

        // Update product list data source after updating tracking line
        this.updateProductListDataSource();
      }
    } else {
      // Check if identical tracking line exists (by SKU, lot, and expiry only)
      const existingLine = this.findMatchingTrackingLine(skuCode, lot, expiry);

      if (existingLine) {
        // Validate quantity before increasing
        const validation = this.validateQuantityNotExceedingPlanned(
          skuCode,
          quantity,
          undefined // Don't exclude any line since we're adding to existing
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlannedError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }

        // Merge serials if both have serials - keep ALL serials including duplicates
        if (serial && existingLine.serial) {
          const existingSerials = existingLine.serial.split(',').map((s: string) => s.trim()).filter((s: string) => s);
          const newSerials = serial.split(',').map((s: string) => s.trim()).filter((s: string) => s);
          // Keep all serials including duplicates - don't use Set to remove duplicates
          const mergedSerials = [...existingSerials, ...newSerials];
          existingLine.serial = mergedSerials.join(',');
        } else if (serial) {
          // If only new line has serial, use it
          existingLine.serial = serial;
        }

        existingLine.quantity = (existingLine.quantity || 0) + quantity;

        this.refreshTrackingLinesUI();

        // Update product list data source after increasing quantity
        this.updateProductListDataSource();
      } else {
        // Validate quantity before creating new line
        const validation = this.validateQuantityNotExceedingPlanned(
          skuCode,
          quantity,
          undefined
        );

        if (!validation.isValid) {
          this.showQuantityExceedsPlannedError(
            validation,
            quantity,
            skuCode,
            selectedItem.skuName
          );
          return;
        }
        const newTrackingLine: TrackingLine = {
          id: Date.now().toString(),
          skuCode: skuCode,
          skuName: selectedItem.skuName || '',
          spuCode: selectedItem.spuCode || '',
          lot: lot,
          lotFromBarcode: this.lotFromBarcode,
          expiry: expiry,
          expiryDate: expiryDate,
          serial: serial,
          quantity: quantity,
          plannedQty: selectedItem.plannedQty,
          policy: selectedItem.policy,
        };

        // Clear lotFromBarcode after use
        this.lotFromBarcode = undefined;

        // Add to tracking lines
        this.trackingLines.push(newTrackingLine);

        // Update groups and UI
        this.refreshTrackingLinesUI();
      }
    }

    // Update product list data source after adding tracking line
    this.updateProductListDataSource();

    // Reset form
    this.resetTrackingLineForm();
    this.lotFromBarcode = undefined;

    if (this.editingTrackingLineId) {
      this.editingTrackingLineId = null;
      this.refreshTableData();
    }
    this.lotInput = '';
    this.expiryDateInput = null;
    this.serialInput = '';
    this.quantityInput = 1;
    this.formBarcodeInput = '';
  }

  /**
   * Persist an audit-trail entry for a barcode scan attempt (matched, not found,
   * or matched-but-not-in-warrant) — written immediately when the scan result is
   * known, not deferred to "Thêm", so even abandoned corrections leave a trace.
   * Fire-and-forget: a secondary audit side-effect must never block or fail the
   * core receiving workflow, so errors are only logged, never surfaced to the user.
   */
  private recordBarcodeScanLog(params: {
    scannedBarcode: string;
    scannedSkuCode?: string;
    skuCode?: string;
    skuName?: string;
    lot?: string;
    expiry?: string;
    serial?: string;
    status: string;
  }): void {
    const warrantIdNum = this.warrantId ? parseInt(this.warrantId, 10) : undefined;
    const dto: BarcodeCorrectionLogCreateDTO = {
      warehouseWarrantId: isNaN(warrantIdNum as number) ? undefined : warrantIdNum,
      warehouseWarrantCode: this.warrantCode || undefined,
      scannedBarcode: params.scannedBarcode,
      scannedSkuCode: params.scannedSkuCode,
      skuCode: params.skuCode,
      skuName: params.skuName,
      lotValue: params.lot,
      expiryDate: params.expiry,
      serialValue: params.serial,
      status: params.status,
    };

    this.barcodeCorrectionLogControllerService
      .create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => console.warn('Failed to record barcode scan log:', error?.status ?? error),
      });
  }

  /**
   * Edit tracking line - populate form with tracking line data
   */
  onEditTrackingLine(rowData: any): void {
    // Find the tracking line
    const trackingLine = this.trackingLines.find(line => line.id === rowData.id);
    if (!trackingLine) {
      return;
    }
    const product = this.items.find(item => item.skuCode === trackingLine.skuCode);
    if (!product) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('inbound.receive.product_not_found'),
        life: 3000,
      });
      return;
    }
    this.editingTrackingLineId = trackingLine.id;
    const serialsArray = trackingLine.serial
      ? trackingLine.serial.split(',').map(s => s.trim()).filter(s => s)
      : [];

    this.addTrackingLineForm.patchValue({
      product: product,
      lot: trackingLine.lot || '',
      expiryDate: trackingLine.expiryDate || (trackingLine.expiry ? new Date(trackingLine.expiry) : null),
      serial: serialsArray,
      quantity: trackingLine.quantity || 1,
    });

    if (product.skuCode) {
      this.loadLotNumbers(product.skuCode);
    }
    this.applyLotExpiryLock(trackingLine.skuCode, trackingLine.lot, this.editingTrackingLineId);
    this.updateFieldValidators(product);

    this.refreshTableData();
    const formElement = document.querySelector('.add-tracking-line-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Cancel edit mode - reset form and clear editing state
   */
  onCancelEdit(): void {
    this.editingTrackingLineId = null;
    this.addTrackingLineForm.get('expiryDate')?.enable();
    this.addTrackingLineForm.reset({
      product: null,
      lot: '',
      expiryDate: null,
      serial: [],
      quantity: 1,
      barcode: '',
    });
    this.selectedProduct = null;
    this.previousProductSkuCode = null;
    this.lotOptions = [];
    this.lotInput = '';
    this.expiryDateInput = null;
    this.serialInput = '';
    this.quantityInput = 1;
    this.formBarcodeInput = '';
    this.refreshTableData();

    this.cdr.markForCheck();
  }

  /**
   * Find matching tracking line based on skuCode, lot, expiry, and serial
   */
  private findMatchingTrackingLine(
    skuCode: string,
    lot?: string,
    expiry?: string
  ): TrackingLine | undefined {
    return this.trackingLines.find(line => {
      // Normalize values for comparison (handle undefined, null, empty string)
      const lineLot = line.lot?.trim() || undefined;
      const lineExpiry = line.expiry?.trim() || undefined;
      const inputLot = lot?.trim() || undefined;
      const inputExpiry = expiry?.trim() || undefined;

      return (
        line.skuCode === skuCode &&
        lineLot === inputLot &&
        lineExpiry === inputExpiry
      );
    });
  }

  /**
   * Update tracking line groups by SKU
   */
  private updateTrackingLineGroups(): void {
    const groupMap = new Map<string, TrackingLineGroup>();

    this.trackingLines.forEach(line => {
      const key = line.skuCode;
      if (!groupMap.has(key)) {
        // Auto-expand groups with multiple lots
        const shouldExpand = this.expandedSkuGroups.has(key) ||
          (this.trackingLines.filter(l => l.skuCode === key).length > 1);
        if (shouldExpand && !this.expandedSkuGroups.has(key)) {
          this.expandedSkuGroups.add(key);
        }

        groupMap.set(key, {
          skuCode: line.skuCode,
          skuName: line.skuName,
          spuCode: line.spuCode,
          lines: [],
          totalQuantity: 0,
          isExpanded: this.expandedSkuGroups.has(key),
        });
      }

      const group = groupMap.get(key)!;
      group.lines.push(line);
      group.totalQuantity += line.quantity;
    });

    this.trackingLineGroups = Array.from(groupMap.values());

    this.flattenTableData();
  }

  /**
   * Flatten tracking line groups into table data source
   */
  private flattenTableData(): void {
    const flatData: any[] = [];

    this.trackingLineGroups.forEach(group => {
      // Find the item to get receivedQty
      const item = this.items.find(i => i.skuCode === group.skuCode);
      const receivedQty = this.getReceivedQuantity(item);

      // If only one lot, show as single row (no group header)
      if (group.lines.length === 1) {
        const line = group.lines[0];
        flatData.push({
          ...line,
          expiry: line.expiryDate || (line.expiry ? new Date(line.expiry) : undefined),
          receivedQty: receivedQty,
          isGroupHeader: false,
          isEditing: this.editingTrackingLineId === line.id,
        });
      } else {
        // Multiple lots - show group header
        flatData.push({
          id: `group-${group.skuCode}`,
          skuCode: group.skuCode,
          skuName: group.skuName,
          spuCode: group.spuCode,
          totalQuantity: group.totalQuantity,
          plannedQty: group.lines[0]?.plannedQty,
          receivedQty: receivedQty,
          isGroupHeader: true,
          isExpanded: group.isExpanded,
        });

        // Add sub-rows if expanded
        if (group.isExpanded) {
          group.lines.forEach(line => {
            const { plannedQty, ...lineWithoutPlannedQty } = line;
            const subRowData: any = {
              ...lineWithoutPlannedQty,
              expiry: line.expiryDate || (line.expiry ? new Date(line.expiry) : undefined),
              // Don't set receivedQty for sub-rows (same as plannedQty)
              isGroupHeader: false,
              isSubRow: true,
              isEditing: this.editingTrackingLineId === line.id,
            };
            flatData.push(subRowData);
          });
        }
      }
    });

    this.tableDataSource = flatData;
    this.cdr.detectChanges();
  }

  private syncTrackingLinesToFormArray(): void {
    // Clear existing form array
    while (this.trackingLinesFormArray.length !== 0) {
      this.trackingLinesFormArray.removeAt(0);
    }

    // Add form controls for each tracking line
    this.trackingLines.forEach((line) => {
      const policy = line.policy;
      const validators: any[] = [];

      // Add required validators based on policy
      if (policy?.requireLot) {
        validators.push(Validators.required);
      }
      if (policy?.requireExpiry) {
        validators.push(Validators.required);
      }
      if (policy?.requireSerial) {
        validators.push(Validators.required);
      }

      // Quantity validators
      const quantityValidators = [Validators.min(0)];
      if (line.plannedQty !== undefined && line.plannedQty !== null) {
        const group = this.trackingLineGroups.find(g => g.skuCode === line.skuCode);
        const maxQty = group ? line.plannedQty - (group.totalQuantity - line.quantity) : line.plannedQty;
        quantityValidators.push(Validators.max(maxQty));
      }

      const lineGroup = this.fb.group({
        id: [line.id],
        skuCode: [line.skuCode],
        skuName: [line.skuName],
        spuCode: [line.spuCode],
        lot: [line.lot || '', policy?.requireLot ? Validators.required : null],
        expiry: [line.expiryDate || (line.expiry ? new Date(line.expiry) : null), policy?.requireExpiry ? Validators.required : null],
        serial: [line.serial || '', policy?.requireSerial ? Validators.required : null],
        quantity: [line.quantity || 0, quantityValidators],
        plannedQty: [line.plannedQty],
        policy: [line.policy],
      });

      this.trackingLinesFormArray.push(lineGroup);
    });
  }

  toggleSkuGroup(skuCode: string): void {
    if (this.expandedSkuGroups.has(skuCode)) {
      this.expandedSkuGroups.delete(skuCode);
    } else {
      this.expandedSkuGroups.add(skuCode);
    }

    this.trackingLineGroups.forEach(group => {
      if (group.skuCode === skuCode) {
        group.isExpanded = this.expandedSkuGroups.has(skuCode);
      }
    });

    this.refreshTableData();
  }

  removeTrackingLine(line: TrackingLine): void {
    this.trackingLines = this.trackingLines.filter((l) => l.id !== line.id);
    this.refreshTrackingLinesUI();
    this.updateProductListDataSource();
  }

  removeSkuGroup(skuCode: string): void {
    this.trackingLines = this.trackingLines.filter((l) => l.skuCode !== skuCode);
    this.expandedSkuGroups.delete(skuCode);
    this.refreshTrackingLinesUI();
    this.updateProductListDataSource();
  }

  /**
   * Lookup product by barcode and fill form with product info
   */
  private lookupProductByBarcode(barcode: string): void {
    this.scanningBarcode = true;
    // Disable via the FormControl API, not a plain [disabled] attribute — mixing
    // the two on a formControlName element makes Angular re-verify the control
    // on every change-detection pass, which got expensive under repeated scans.
    this.addTrackingLineForm.get('barcode')?.disable({ emitEvent: false });
    const trimmedBarcode = barcode.trim();

    // Resolve the barcode to a SKU first. The resolver response already carries
    // gs1ParseResult when the scan resolved via GS1-GTIN fallback (matchType
    // GS1_GTIN), so the common case needs only this one call. matchType EXACT
    // never carries gs1ParseResult (goods-service only parses it for the GTIN
    // fallback path), so an exact match on a barcode that's itself a GS1 string —
    // e.g. re-scanning a lot/expiry that was already registered — falls back to
    // the dedicated GS1-parse endpoint below so lot/expiry/serial auto-fill (AC-04)
    // still works for that case.
    this.barcodeResolverService.getSkuByBarcode(trimmedBarcode).pipe(
      switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
      catchError((error) => {
        // Not found is a routine, UI-handled outcome here (correction panel),
        // so log the status only — logging the full response object on every
        // failed scan piles up retained references in the console and slows
        // the page down over a long scanning session.
        console.warn('SKU lookup by barcode returned no match:', error?.status ?? error);
        return of(null);
      }),
      switchMap((skuDetail: BarcodeResolveResultVO | null) => {
        if (skuDetail?.gs1ParseResult) {
          return of({ skuDetail, parseResult: skuDetail.gs1ParseResult as Gs1ParseResultVO });
        }
        if (!skuDetail) {
          return of({ skuDetail, parseResult: null as Gs1ParseResultVO | null });
        }
        return this.barcodeResolverService.getGs1ParseResult(trimmedBarcode).pipe(
          switchMap((response: any) => from(BlobConverterUtil.convertToJson(response))),
          catchError((error) => {
            console.warn('GS1 parse failed for scanned barcode:', error?.status ?? error);
            return of(null);
          }),
          map((parseResult) => ({ skuDetail, parseResult: parseResult as Gs1ParseResultVO | null }))
        );
      })
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.scanningBarcode = false;
        this.addTrackingLineForm.get('barcode')?.enable({ emitEvent: false });

        const skuDetail = result.skuDetail as BarcodeResolveResultVO | null;
        const parseResult = result.parseResult as Gs1ParseResultVO | null;

        if (!skuDetail || !skuDetail.skuCode) {
          // Correction panel carries this message, so no separate toast.
          this.pendingBarcodeCorrection = {
            scannedBarcode: trimmedBarcode,
            message: this.translateService.instant(
              'inbound.receive.correct_barcode_prompt_not_found',
              { barcode: trimmedBarcode }
            ),
          };
          // Audit every scan attempt, success or failure — logged now, not
          // deferred to "Thêm", so an abandoned correction still leaves a trace.
          this.recordBarcodeScanLog({
            scannedBarcode: trimmedBarcode,
            status: BARCODE_CORRECTION_STATUS.NOT_FOUND,
          });
          return;
        }

        // Find matching item in warrant items
        const matchingItem = this.items.find(
          item => item.skuCode === skuDetail.skuCode
        );

        if (!matchingItem) {
          // Correction panel carries this message, so no separate toast.
          this.pendingBarcodeCorrection = {
            scannedBarcode: trimmedBarcode,
            message: this.translateService.instant(
              'inbound.receive.correct_barcode_prompt_not_in_warrant',
              { barcode: trimmedBarcode, sku: skuDetail.skuCode }
            ),
            scannedSkuCode: skuDetail.skuCode,
          };
          this.recordBarcodeScanLog({
            scannedBarcode: trimmedBarcode,
            scannedSkuCode: skuDetail.skuCode,
            skuName: skuDetail.skuName,
            status: BARCODE_CORRECTION_STATUS.NOT_IN_WARRANT,
          });
          return;
        }

        // A later successful scan supersedes any earlier correction prompt.
        this.pendingBarcodeCorrection = null;

        // Set selected product
        this.selectedProduct = matchingItem;
        this.previousProductSkuCode = matchingItem.skuCode || null;

        // Prefer goods-service GS1 parse; fall back to client auto-detect (GS1 / CUSTOM).
        const clientParsed = parseScannedBarcode(trimmedBarcode);
        const lotFromBarcode = parseResult?.lot || clientParsed.batch || '';
        const expiry = parseResult?.expiry || null;
        const serial = parseResult?.serial || '';

        // Convert expiry string (YYMMDD or ISO) to Date if available
        const expiryDate =
          parseGS1DateToDate(expiry) || isoDateToLocalDate(clientParsed.expiry);

        // Product is still usable, but staff must know Lot/Expiry/Serial were not
        // auto-filled so they don't Add with a wrong default quantity unaware (AC-04).
        if (!lotFromBarcode && !expiryDate && !serial) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.messages.warning'),
            detail: this.translateService.instant('inbound.receive.gs1_fields_not_extracted'),
            life: 4000,
          });
        }

        // Convert serial to array if it's a string
        const serials = serial
          ? (typeof serial === 'string'
              ? serial.split(',').map(s => s.trim()).filter(s => s)
              : Array.isArray(serial) ? serial : [])
          : [];

        // Auto-accumulate: if a tracking line already exists for this SKU/lot/expiry,
        // bump its quantity immediately instead of waiting for the user to click "Add"
        // again (RBA-2288 AC-02) — leaves any in-progress form edits for other lines untouched.
        // Tracking lines store expiry as an ISO YYYY-MM-DD string (see onAddTrackingLine), not
        // the raw GS1 YYMMDD string, so normalize before comparing.
        const expiryIso = expiryDate ? formatDate(expiryDate) : undefined;
        const existingLine = this.findMatchingTrackingLine(
          matchingItem.skuCode || '',
          lotFromBarcode,
          expiryIso
        );

        // Audit every scan attempt, success or failure — logged right when the
        // match is determined, whether it auto-accumulates into an existing
        // line below or still needs the user to click "Thêm".
        this.recordBarcodeScanLog({
          scannedBarcode: trimmedBarcode,
          skuCode: matchingItem.skuCode,
          skuName: matchingItem.skuName,
          lot: lotFromBarcode || undefined,
          expiry: expiryIso,
          serial: serials.length > 0 ? serials.join(',') : undefined,
          status: BARCODE_CORRECTION_STATUS.MATCHED,
        });

        if (existingLine) {
          const incrementQty = serials.length > 0 ? serials.length : 1;
          const validation = this.validateQuantityNotExceedingPlanned(
            matchingItem.skuCode || '',
            incrementQty
          );

          if (!validation.isValid) {
            this.showQuantityExceedsPlannedError(
              validation,
              incrementQty,
              matchingItem.skuCode || '',
              matchingItem.skuName
            );
            return;
          }

          if (serials.length > 0 && existingLine.serial) {
            const existingSerials = existingLine.serial.split(',').map(s => s.trim()).filter(s => s);
            existingLine.serial = [...existingSerials, ...serials].join(',');
          } else if (serials.length > 0) {
            existingLine.serial = serials.join(',');
          }

          existingLine.quantity = (existingLine.quantity || 0) + incrementQty;

          this.refreshTrackingLinesUI();
          this.updateProductListDataSource();

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('inbound.receive.quantity_increased'),
            life: 3000,
          });

          this.addTrackingLineForm.patchValue({ barcode: '' });
          this.formBarcodeInput = '';
          return;
        }

        // Store lotFromBarcode for later use when creating tracking line
        this.lotFromBarcode = lotFromBarcode;

        // Calculate remaining quantity for the selected product
        const remainingQty = this.getRemainingQuantityForItem(matchingItem);

        // Update form with product and parsed barcode data
        this.addTrackingLineForm.patchValue({
          product: matchingItem,
          lot: lotFromBarcode,
          expiryDate: expiryDate,
          serial: serials,
          quantity: serials.length > 0 ? serials.length : Math.max(1, remainingQty),
          barcode: '',
        });

        // Update quantity validators
        const quantityControl = this.addTrackingLineForm.get('quantity');
        const validators = [
          Validators.required,
          Validators.min(1),
          Validators.max(remainingQty)
        ];
        quantityControl?.setValidators(validators);
        quantityControl?.updateValueAndValidity({ emitEvent: false });

        // Load lot numbers for the selected product
        if (matchingItem.skuCode) {
          this.loadLotNumbers(matchingItem.skuCode);
        }

        // The patchValue above set expiryDate straight from the scanned GS1
        // data - if this lot already has a known expiry (server-side or from
        // an earlier not-yet-submitted line in this session), that value must
        // win instead and the field locked, same as manual lot selection.
        this.applyLotExpiryLock(matchingItem.skuCode, lotFromBarcode, this.editingTrackingLineId);

        // Update field validators based on policy
        this.updateFieldValidators(matchingItem);
      },
      error: (error) => {
        this.scanningBarcode = false;
        this.addTrackingLineForm.get('barcode')?.enable({ emitEvent: false });
        console.error('Error in barcode lookup:', error);
        this.showErrorMessage('inbound.receive.error_finding_sku');
      }
    });
  }

  onFormBarcodeInput(): void {
    // Get barcode value from form control or ngModel
    const formBarcode = this.addTrackingLineForm.get('barcode')?.value;
    const ngModelBarcode = this.formBarcodeInput;
    const barcodeValue = (formBarcode || ngModelBarcode || '').toString().trim();

    if (!barcodeValue) {
      this.showErrorMessage('inbound.receive.barcode_empty');
      return;
    }

    // Call API to lookup product by barcode and fill form
    this.lookupProductByBarcode(barcodeValue);
  }

  /** Dismiss the correction prompt without applying an override. */
  onCancelBarcodeCorrection(): void {
    this.pendingBarcodeCorrection = null;
  }

  startCamera(): void {
    // TODO: Implement camera start
  }

  stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.cameraActive = false;
  }

  /**
   * Transform tracking lines to batch payload format for API
   * Groups by SKU, then by LOT, aggregates quantities and serials
   */
  private transformTrackingLinesToBatchPayload(
    trackingLines: TrackingLine[],
    discrepancyEntries: DiscrepancyEntry[] = [],
  ): WarehouseWarrantSubmitRequestDTO {
    const discrepancyMap = new Map(
      discrepancyEntries.map((e) => [e.skuCode, e]),
    );
    // Group by SKU
    const skuMap = new Map<string, TrackingLine[]>();
    trackingLines.forEach((line) => {
      if (!skuMap.has(line.skuCode)) {
        skuMap.set(line.skuCode, []);
      }
      skuMap.get(line.skuCode)!.push(line);
    });

    // Transform to payload format
    const details: WarehouseWarrantSubmitReqDetailDTO[] = Array.from(skuMap.entries()).map(([skuCode, lines]) => {
      // Calculate total received quantity for this SKU
      const receivedQuantity = lines.reduce(
        (sum, line) => sum + (line.quantity || 0),
        0
      );

      // Group by LOT (lotNumber + expiryDate combination)
      const lotMap = new Map<string, TrackingLine[]>();
      lines.forEach((line) => {
        const lotKey = `${line.lot || ''}_${line.expiry || ''}`;
        if (!lotMap.has(lotKey)) {
          lotMap.set(lotKey, []);
        }
        lotMap.get(lotKey)!.push(line);
      });

      // Transform to batches and collect serials per batch
      // Important: If batch has serials, number of serials must equal batch qty (each serial = qty 1)
      const batches: WarehouseWarrantSubmitReqBatchDTO[] = [];
      const allSerials: SerialDetailDTO[] = [];

      Array.from(lotMap.entries()).forEach(([lotKey, lotLines]) => {
        const firstLine = lotLines[0];
        const expiryDate = firstLine.expiry || undefined;
        // batchId = lot (from user selection in form)
        const batchId = firstLine.lot || undefined;
        // lotNumber = lot from barcode if available, otherwise use batchId (lot from form)
        const lotNumber = firstLine.lotFromBarcode || batchId;

        // Collect serials for this batch
        const batchSerials: SerialDetailDTO[] = [];
        let batchQty = 0;

        lotLines.forEach((line) => {
          const lineQty = line.quantity || 0;
          batchQty += lineQty;

          // If line has serial, create serials based on quantity
          // Serial can be comma-separated string, split it into array
          if (line.serial) {
            const serialsArray = line.serial.split(',').map(s => s.trim()).filter(s => s);

            // If we have serials from input, use them (each serial = qty 1)
            if (serialsArray.length > 0) {
              serialsArray.forEach(serialNumber => {
                batchSerials.push({
                  serialNumber: serialNumber,
                  batchId: batchId, // Link serial to batchId (lot from form)
                  expiryDate: expiryDate,
                });
              });
            }
          }
        });
        if (batchId || lotNumber) {
          const batch: WarehouseWarrantSubmitReqBatchDTO = {
            batchId: batchId, // lot (from form/user selection)
            lotNumber: lotNumber, // lot from barcode if available, otherwise = batchId
            expiryDate: expiryDate,
            qty: batchQty,
          };

          batches.push(batch);
        }

        // Add serials to allSerials array
        allSerials.push(...batchSerials);
      });

      // Validate: If there are serials, total serials count must match total quantity
      const totalSerialsCount = allSerials.length;
      if (totalSerialsCount > 0 && totalSerialsCount !== receivedQuantity) {
        console.warn(
          `Warning: Serial count (${totalSerialsCount}) does not match received quantity (${receivedQuantity}) for SKU ${skuCode}`
        );
        // Still proceed, but log warning
      }

      const discrepancy = discrepancyMap.get(skuCode);
      const detail: WarehouseWarrantSubmitReqDetailDTO & {
        discrepancyReason?: string;
        discrepancyNote?: string;
      } = {
        skuCode,
        receivedQuantity,
        batches: batches.length > 0 ? batches : undefined,
        serials: allSerials.length > 0 ? allSerials : undefined,
        ...(discrepancy
          ? {
              discrepancyReason: discrepancy.reason.trim(),
              discrepancyNote: discrepancy.note.trim() || undefined,
            }
          : {}),
      };
      return detail;
    });

    return { details };
  }
}

